import React, { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const LABELS = { vendor: "store", date: "date" };

export const pendingMissing = (expense) => (
  expense?.receipt_followup?.status === "pending" ? (expense.receipt_followup.missing || []) : []
);

export const ReceiptFollowupBadge = ({ expense, testId }) => {
  const missing = pendingMissing(expense);
  if (!missing.length) return null;
  return (
    <span className="ml-1.5 inline-block text-[9px] px-1 py-0.5 rounded-sm bg-amber-100 text-amber-900 font-semibold uppercase tracking-wider"
      title="The receipt couldn't be read — these details are still needed" data-testid={testId}>
      Needs {missing.map(field => LABELS[field] || field).join(" & ")}
    </span>
  );
};

export const AmountMismatchBadge = ({ expense, testId }) => {
  const mismatch = expense?.amount_mismatch ?? expense?.ai_audit?.amount_mismatch;
  if (!mismatch) return null;
  const receiptTotal = expense.receipt_total ?? expense.ai_audit?.receipt_total;
  return (
    <span className="ml-1.5 inline-flex items-center gap-1 whitespace-nowrap text-[9px] px-1 py-0.5 rounded-sm bg-red-600 text-white font-bold uppercase tracking-wider"
      title="The amount entered doesn't match the receipt total" data-testid={testId}>
      <AlertTriangle className="w-3 h-3 shrink-0" aria-hidden="true" />
      Receipt says ${Math.abs(Number(receiptTotal || 0)).toFixed(2)}
    </span>
  );
};

export const ReceiptDetailsFix = ({ expense, onFixed, autoFocus = false }) => {
  const missing = pendingMissing(expense);
  const [vendor, setVendor] = useState("");
  const [date, setDate] = useState(missing.includes("date") ? "" : expense.date || "");
  const [saving, setSaving] = useState(false);
  if (!missing.length) return null;
  const save = async () => {
    if (missing.includes("vendor") && !vendor.trim()) return toast.error("Enter the store name.");
    if (missing.includes("date") && !date) return toast.error("Enter the purchase date.");
    setSaving(true);
    try {
      const { data } = await api.patch(`/expenses/${expense.id}/receipt-details`, { vendor: vendor.trim(), date: missing.includes("date") ? date : "" });
      toast.dismiss();
      toast.success("Thanks — details saved.");
      onFixed(data);
    } catch (error) {
      toast.error(formatErr(error.response?.data?.detail));
    } finally { setSaving(false); }
  };
  return (
    <div className="mt-2 space-y-2 rounded-sm border border-amber-300 bg-amber-50 p-2" data-testid={`receipt-fix-${expense.id}`}>
      <p className="text-xs text-amber-900">We couldn't read this from the receipt. Please add it:</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {missing.includes("vendor") && <div><Label className="text-xs">Store name *</Label>
          <Input value={vendor} onChange={event => setVendor(event.target.value)} autoFocus={autoFocus} className="h-8" data-testid={`receipt-fix-vendor-${expense.id}`} /></div>}
        {missing.includes("date") && <div><Label className="text-xs">Purchase date *</Label>
          <Input type="date" value={date} onChange={event => setDate(event.target.value)} autoFocus={autoFocus && !missing.includes("vendor")} className="h-8" data-testid={`receipt-fix-date-${expense.id}`} /></div>}
      </div>
      <Button size="sm" className="h-7 rounded-sm" onClick={save} disabled={saving} data-testid={`receipt-fix-save-${expense.id}`}>{saving ? "Saving…" : "Save details"}</Button>
    </div>
  );
};
